import { useMutation, useQueryClient } from '@tanstack/react-query';
import creatorProgramAPI from '../../../APIs/creatorProgramAPI';
import { notification } from '../../../commonComponents/Notification/Notification';
import { downloadFile } from '../../Analytics/MerchantAnalytics/Constant';
import { EXPORT_FILENAME_PREFIX, QUERY_KEYS } from '../constants';
import { apiErrorBody, getApiErrorMessage } from '../utils/apiError';
import { formatCurrency } from '../utils/formatters';
import { NON_POSITIVE_BALANCE_CODE } from '../utils/payoutStatus';
import type {
  CreateCreatorPayload,
  CreatePayoutPayload,
  CreateTermsPayload,
  Creator,
  CreatorTerms,
  Payout,
  PayoutExportParams,
  UpdateCreatorPayload,
  UpdatePayoutPayload,
} from '../types';

/**
 * §2.1 — create a creator.
 *
 * `400` on a duplicate email or a code prefix already used by another creator at
 * this merchant; the message is surfaced so the form can stay open and be corrected.
 */
export function useCreateCreator() {
  const queryClient = useQueryClient();

  return useMutation<Creator, unknown, CreateCreatorPayload>({
    mutationFn: (payload) => creatorProgramAPI.createCreator(payload),
    onSuccess: (creator) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CREATORS] });
      notification.success(
        'Creator created',
        `${creator?.name || 'The creator'} was added to your program.`
      );
    },
    onError: (error) => {
      notification.error(
        'Unable to create creator',
        getApiErrorMessage(error, 'The creator could not be created.')
      );
    },
  });
}

/**
 * §2.4 — update a creator's status and/or code prefix.
 *
 * These are the only two updatable fields; `UpdateCreatorPayload` enforces that at
 * the type level. Both are independent — send just the one that changed.
 */
export function useUpdateCreator() {
  const queryClient = useQueryClient();

  return useMutation<Creator, unknown, { id: string; payload: UpdateCreatorPayload }>({
    mutationFn: ({ id, payload }) => creatorProgramAPI.updateCreator(id, payload),
    onSuccess: (_creator, { id }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CREATORS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CREATOR, id] });
      // Status and prefix both feed the performance reports.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEADERBOARD] });
      notification.success('Creator updated', 'The changes were saved.');
    },
    onError: (error) => {
      notification.error(
        'Unable to update creator',
        getApiErrorMessage(error, 'The changes could not be saved.')
      );
    },
  });
}

/**
 * §2.5 — set new commission terms.
 *
 * Terms are immutable and versioned: this always creates a brand-new row and
 * immediately supersedes whatever was current. There is deliberately no update
 * counterpart, and a mid-cycle change never retroactively touches codes or orders
 * created under the old terms.
 *
 * Invalidates the creator (its `terms` history grew) and the performance queries,
 * since a new allowance changes what subsequent orders earn.
 */
export function useCreateTerms() {
  const queryClient = useQueryClient();

  return useMutation<CreatorTerms, unknown, { id: string; payload: CreateTermsPayload }>({
    mutationFn: ({ id, payload }) => creatorProgramAPI.createTerms(id, payload),
    onSuccess: (_terms, { id }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CREATOR, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CREATORS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.CREATOR_PERFORMANCE, id] });
      notification.success(
        'New terms version created',
        'These terms are now current. The previous version has been superseded.'
      );
    },
    onError: (error) => {
      notification.error(
        'Unable to set terms',
        getApiErrorMessage(error, 'The terms could not be saved.')
      );
    },
  });
}

/**
 * §5.1 — draft a payout.
 *
 * Sweeps every currently-`payable` row for the creator created before `periodEnd`
 * into a new `draft`, locking those rows to it. `periodStart` is bookkeeping/display
 * only — it is NOT a query bound, so the sweep can include rows older than it.
 *
 * A `422` with `code: "non_positive_payout_balance"` means there was simply nothing
 * payable to draft. That's an ordinary outcome, not a failure, so the balance from
 * the error body is surfaced instead of a generic error.
 */
export function useDraftPayout() {
  const queryClient = useQueryClient();

  return useMutation<Payout, unknown, CreatePayoutPayload>({
    mutationFn: (payload) => creatorProgramAPI.createPayout(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYOUTS] });
      // Swept rows now carry a payoutId, so the ledger is stale.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ATTRIBUTIONS] });
      notification.success('Payout drafted', 'Payable rows were swept into a new draft.');
    },
    onError: (error) => {
      const body = apiErrorBody(error);
      if (body?.code === NON_POSITIVE_BALANCE_CODE) {
        // The server names this `outstandingBalance`; `balance` is only a fallback.
        const balance = body.outstandingBalance ?? body.balance;
        notification.warning(
          'Nothing to pay out',
          balance != null
            ? `This creator's payable balance is ${formatCurrency(
                balance
              )} for the selected period. There is nothing to draft.`
            : 'This creator has no payable rows for the selected period.'
        );
        return;
      }
      notification.error(
        'Unable to draft payout',
        getApiErrorMessage(error, 'The payout could not be drafted.')
      );
    },
  });
}

/**
 * §5.4 — advance a payout's status.
 *
 * Callers must only offer transitions allowed by `PAYOUT_TRANSITIONS`; anything else
 * is a 422 server-side. Re-sending the status a payout already has is a documented
 * no-op success, so a double-submit is safe.
 *
 * Moving to `paid` flips every swept attribution row `payable → paid`; cancelling
 * releases them back to `payoutId: null` for the next draft. Either way the ledger
 * changes, so it's invalidated alongside the payout list.
 */
export function useUpdatePayoutStatus() {
  const queryClient = useQueryClient();

  return useMutation<Payout, unknown, { id: string; payload: UpdatePayoutPayload }>({
    mutationFn: ({ id, payload }) => creatorProgramAPI.updatePayout(id, payload),
    onSuccess: (_payout, { id, payload }) => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYOUTS] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PAYOUT, id] });
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.ATTRIBUTIONS] });

      const messages: Record<string, string> = {
        approved: 'Payout approved. No transfer is triggered by this action.',
        paid: 'Payout marked as paid. Its attributions are now paid.',
        cancelled:
          'Payout cancelled. Its rows were released and can be swept into the next draft.',
      };
      notification.success('Payout updated', messages[payload.status] || 'The payout was updated.');
    },
    onError: (error) => {
      notification.error(
        'Unable to update payout',
        getApiErrorMessage(error, 'The payout could not be updated.')
      );
    },
  });
}

/**
 * §5.5 — CSV export.
 *
 * The endpoint streams `text/csv` with a `Content-Disposition: attachment` header but
 * still requires the JWT, so it is fetched as an authenticated blob and handed to the
 * shared `downloadFile` helper. Opening the raw URL in a new window would drop the
 * auth header and fail.
 */
export function useExportPayouts() {
  return useMutation<Blob, unknown, PayoutExportParams>({
    mutationFn: (params) => creatorProgramAPI.exportPayouts(params),
    onSuccess: (blob, params) => {
      const suffix = params.detail === 1 ? '-detail' : '';
      const range = [params.startDate, params.endDate].filter(Boolean).join('_to_');
      const stamp = range || new Date().toISOString().split('T')[0];
      downloadFile(blob, `${EXPORT_FILENAME_PREFIX}${suffix}-${stamp}.csv`);
      notification.success('Export ready', 'The CSV has been downloaded.');
    },
    onError: (error) => {
      notification.error(
        'Export failed',
        getApiErrorMessage(error, 'The CSV could not be exported.')
      );
    },
  });
}
